import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search,
  X,
  User,
  Hash,
  Image,
  Clock,
  TrendingUp,
  ArrowUp,
  ArrowDown,
  ArrowRight
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import PrivacyService, { UserSearchResult } from '../../services/privacy';
import { FilterOptions } from '../../types';

// Define search types
type SearchType = 'all' | 'users' | 'tags' | 'images';

interface SearchResult {
  type: 'user' | 'tag' | 'image';
  id: string;
  title: string;
  subtitle?: string;
  avatar?: string;
  preview?: string;
  metadata?: any;
}

interface TagSearchResult {
  tag: string;
  category: string;
  count: number;
  description?: string;
}

interface ImageSearchResult {
  id: string;
  title: string;
  description?: string;
  prompt_text: string;
  image_url: string;
  user_id: string;
  username?: string;
  display_name?: string;
  avatar_url?: string;
  tags: Array<{
    name: string;
    category: string;
  }>;
  is_public: boolean;
  likes_count: number;
  views_count: number;
  created_at: string;
  relevance_score: number;
}

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
  onUserSelect?: (user: UserSearchResult) => void;
  onTagSelect?: (tag: string, category: string) => void;
  onImageSelect?: (image: ImageSearchResult) => void;
  initialQuery?: string;
  initialType?: SearchType;
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({
  isOpen,
  onClose,
  onUserSelect,
  onTagSelect,
  onImageSelect,
  initialQuery = '',
  initialType = 'all'
}) => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [searchType, setSearchType] = useState<SearchType>(initialType);
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [userResults, setUserResults] = useState<UserSearchResult[]>([]);
  const [tagResults, setTagResults] = useState<TagSearchResult[]>([]);
  const [imageResults, setImageResults] = useState<ImageSearchResult[]>([]);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [popularTags, setPopularTags] = useState<TagSearchResult[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  // Initialize recent searches and popular tags
  useEffect(() => {
    if (isOpen) {
      loadRecentSearches();
      loadPopularTags();
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [isOpen]);

  // Handle search query changes
  useEffect(() => {
    if (searchQuery.length > 0) {
      const debounceTimer = setTimeout(() => {
        handleSearch();
      }, 300);
      return () => clearTimeout(debounceTimer);
    } else {
      setSearchResults([]);
      setUserResults([]);
      setTagResults([]);
      setImageResults([]);
    }
  }, [searchQuery, searchType]);

  // Load recent searches from localStorage
  const loadRecentSearches = () => {
    const saved = localStorage.getItem('toefetti_recent_searches');
    if (saved) {
      setRecentSearches(JSON.parse(saved));
    }
  };

  // Load popular tags (mock data for now, would come from analytics)
  const loadPopularTags = () => {
    const mockPopularTags: TagSearchResult[] = [
      { tag: 'red', category: 'nailColor', count: 1250, description: 'Classic red nail polish' },
      { tag: 'beach', category: 'background', count: 980, description: 'Sandy beach setting' },
      { tag: 'elegant', category: 'style', count: 875, description: 'Sophisticated and refined' },
      { tag: 'stiletto', category: 'nailLength', count: 720, description: 'Sharp, pointed nail shape' },
      { tag: 'anklet', category: 'jewelry', count: 650, description: 'Decorative ankle jewelry' },
      { tag: 'latex', category: 'bondage', count: 580, description: 'Latex material and clothing' },
      { tag: 'close-up', category: 'angle', count: 520, description: 'Detailed close-up shots' },
      { tag: 'gold', category: 'nailColor', count: 480, description: 'Metallic gold polish' }
    ];
    setPopularTags(mockPopularTags);
  };

  // Save search to recent searches
  const saveRecentSearch = (query: string) => {
    const updated = [query, ...recentSearches.filter(s => s !== query)].slice(0, 10);
    setRecentSearches(updated);
    localStorage.setItem('toefetti_recent_searches', JSON.stringify(updated));
  };

  // Detect if search is for users (@username)
  const isUserSearch = (query: string) => {
    return query.startsWith('@');
  };

  // Detect if search is for tags (#tag)
  const isTagSearch = (query: string) => {
    return query.startsWith('#');
  };

  // Main search handler
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsLoading(true);
    
    try {
      const query = searchQuery.trim();
      
      // Determine search type based on query or explicit type
      if (isUserSearch(query) || searchType === 'users') {
        await searchUsers(query.replace('@', ''));
      } else if (isTagSearch(query) || searchType === 'tags') {
        await searchTags(query.replace('#', ''));
      } else if (searchType === 'images') {
        await searchImages(query);
      } else {
        // Search all types
        await Promise.all([
          searchUsers(query),
          searchTags(query),
          searchImages(query)
        ]);
      }
      
      // Combine results for unified display
      combineSearchResults();
      
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Search for users
  const searchUsers = async (query: string) => {
    if (!user) return;
    
    try {
      const results = await PrivacyService.searchUsers(query, 'all', 10);
      setUserResults(results);
    } catch (error) {
      console.error('User search error:', error);
      setUserResults([]);
    }
  };

  // Search for tags based on filter categories
  const searchTags = async (query: string) => {
    // This would normally query a database, but for now we'll search our filter definitions
    const allTags = getAllAvailableTags();
    const filtered = allTags.filter(tag => 
      tag.tag.toLowerCase().includes(query.toLowerCase()) ||
      tag.category.toLowerCase().includes(query.toLowerCase()) ||
      (tag.description && tag.description.toLowerCase().includes(query.toLowerCase()))
    );
    setTagResults(filtered.slice(0, 20));
  };

  // Search for images by tags and content
  const searchImages = async (query: string) => {
    // This would query the database for images with matching tags or content
    // For now, returning mock data
    const mockImages: ImageSearchResult[] = [
      {
        id: '1',
        title: 'Elegant Red Nails',
        description: 'Beautiful red nails with elegant style',
        image_url: '/api/placeholder/300/300',
        prompt_text: 'Beautiful red nails with elegant style',
        tags: [
          { name: 'red', category: 'nailColor' },
          { name: 'elegant', category: 'style' }
        ],
        user_id: 'user1',
        username: 'fashionista',
        display_name: 'Fashion Enthusiast',
        avatar_url: undefined,
        is_public: true,
        likes_count: 42,
        views_count: 156,
        created_at: new Date().toISOString(),
        relevance_score: 0.95
      }
    ];
    
    setImageResults(mockImages.filter(img => 
      img.title.toLowerCase().includes(query.toLowerCase()) ||
      img.prompt_text.toLowerCase().includes(query.toLowerCase()) ||
      img.tags.some(tag => tag.name.toLowerCase().includes(query.toLowerCase()))
    ));
  };

  // Get all available tags from filter definitions
  const getAllAvailableTags = (): TagSearchResult[] => {
    const filterCategories = [
      {
        id: 'background',
        title: 'Background',
        options: [
          { value: 'beach', label: 'Beach', description: 'Sandy beach setting' },
          { value: 'studio', label: 'Studio', description: 'Professional studio backdrop' },
          { value: 'bedroom', label: 'Bedroom', description: 'Intimate bedroom setting' },
          { value: 'outdoor', label: 'Outdoor', description: 'Natural outdoor environment' },
          { value: 'luxury', label: 'Luxury', description: 'High-end luxury setting' },
          { value: 'minimal', label: 'Minimal', description: 'Clean, minimal background' }
        ]
      },
      {
        id: 'nailColor',
        title: 'Nail Polish',
        options: [
          { value: 'red', label: 'Red', description: 'Classic red nail polish' },
          { value: 'pink', label: 'Pink', description: 'Feminine pink shade' },
          { value: 'purple', label: 'Purple', description: 'Rich purple color' },
          { value: 'blue', label: 'Blue', description: 'Bold blue polish' },
          { value: 'green', label: 'Green', description: 'Vibrant green shade' },
          { value: 'black', label: 'Black', description: 'Dramatic black polish' },
          { value: 'white', label: 'White', description: 'Clean white finish' },
          { value: 'gold', label: 'Gold', description: 'Metallic gold polish' },
          { value: 'silver', label: 'Silver', description: 'Shimmering silver' },
          { value: 'french', label: 'French Tips', description: 'Classic French manicure' },
          { value: 'gradient', label: 'Gradient', description: 'Ombre color transition' }
        ]
      },
      {
        id: 'style',
        title: 'Style',
        options: [
          { value: 'elegant', label: 'Elegant', description: 'Sophisticated and refined' },
          { value: 'casual', label: 'Casual', description: 'Relaxed and comfortable' },
          { value: 'sexy', label: 'Sexy', description: 'Alluring and attractive' },
          { value: 'artistic', label: 'Artistic', description: 'Creative and expressive' },
          { value: 'professional', label: 'Professional', description: 'Business-appropriate' },
          { value: 'vintage', label: 'Vintage', description: 'Classic retro style' },
          { value: 'modern', label: 'Modern', description: 'Contemporary and current' }
        ]
      }
      // Add more categories as needed
    ];

    const tags: TagSearchResult[] = [];
    filterCategories.forEach(category => {
      category.options.forEach(option => {
        tags.push({
          tag: option.value,
          category: category.id,
          count: Math.floor(Math.random() * 1000) + 50, // Mock count
          description: option.description
        });
      });
    });

    return tags;
  };

  // Combine all search results into unified format
  const combineSearchResults = () => {
    const combined: SearchResult[] = [];

    // Add user results
    userResults.forEach(user => {
      combined.push({
        type: 'user',
        id: user.user_id,
        title: user.display_name || user.username,
        subtitle: `@${user.username}`,
        avatar: user.avatar_url || undefined,
        metadata: user
      });
    });

    // Add tag results
    tagResults.forEach(tag => {
      combined.push({
        type: 'tag',
        id: `${tag.category}-${tag.tag}`,
        title: tag.tag,
        subtitle: `${tag.category} • ${tag.count} uses`,
        preview: tag.description,
        metadata: tag
      });
    });

    // Add image results
    imageResults.forEach(image => {
      combined.push({
        type: 'image',
        id: image.id,
        title: image.title,
        subtitle: `by @${image.username}`,
        preview: image.image_url,
        metadata: image
      });
    });

    setSearchResults(combined);
  };

  // Handle result selection
  const handleResultSelect = (result: SearchResult) => {
    saveRecentSearch(searchQuery);
    
    switch (result.type) {
      case 'user':
        onUserSelect?.(result.metadata);
        break;
      case 'tag':
        onTagSelect?.(result.metadata.tag, result.metadata.category);
        break;
      case 'image':
        onImageSelect?.(result.metadata);
        break;
    }
    
    onClose();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => Math.min(prev + 1, searchResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && searchResults[activeIndex]) {
        handleResultSelect(searchResults[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setUserResults([]);
    setTagResults([]);
    setImageResults([]);
    setActiveIndex(-1);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center pt-20">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
        {/* Search Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search users (@username), tags (#tag), or images..."
                className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-lg"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Search Type Tabs */}
          <div className="flex space-x-2 mt-4">
            {[
              { key: 'all', label: 'All', icon: Search },
              { key: 'users', label: 'Users', icon: Search },
              { key: 'tags', label: 'Tags', icon: Hash },
              { key: 'images', label: 'Images', icon: Image }
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setSearchType(key as SearchType)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                  searchType === key
                    ? 'bg-purple-100 text-purple-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm font-medium">{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Search Results */}
        <div className="max-h-[60vh] overflow-y-auto">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <span className="ml-3 text-gray-600">Searching...</span>
            </div>
          )}

          {!isLoading && searchQuery && searchResults.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Search className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No results found for "{searchQuery}"</p>
              <p className="text-sm mt-2">Try searching for users with @username or tags with #tag</p>
            </div>
          )}

          {!isLoading && searchResults.length > 0 && (
            <div className="p-4 space-y-2">
              {searchResults.map((result, index) => (
                <button
                  key={result.id}
                  onClick={() => handleResultSelect(result)}
                  className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors text-left ${
                    index === activeIndex
                      ? 'bg-purple-50 border-purple-200'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  {/* Result Icon/Avatar */}
                  <div className="flex-shrink-0">
                    {result.type === 'user' && (
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                        {result.avatar ? (
                          <img 
                            src={result.avatar} 
                            alt={result.title}
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          <User className="w-5 h-5 text-white" />
                        )}
                      </div>
                    )}
                    {result.type === 'tag' && (
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <Hash className="w-5 h-5 text-blue-600" />
                      </div>
                    )}
                    {result.type === 'image' && (
                      <div className="w-10 h-10 bg-gray-100 rounded-lg overflow-hidden">
                        <img 
                          src={result.preview} 
                          alt={result.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                  </div>

                  {/* Result Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <h3 className="font-medium text-gray-900 truncate">{result.title}</h3>
                      {result.type === 'user' && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                          User
                        </span>
                      )}
                      {result.type === 'tag' && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                          Tag
                        </span>
                      )}
                      {result.type === 'image' && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                          Image
                        </span>
                      )}
                    </div>
                    {result.subtitle && (
                      <p className="text-sm text-gray-500 truncate">{result.subtitle}</p>
                    )}
                    {result.preview && result.type === 'tag' && (
                      <p className="text-xs text-gray-400 truncate mt-1">{result.preview}</p>
                    )}
                  </div>

                  <ArrowRight className="w-5 h-5 text-gray-400" />
                </button>
              ))}
            </div>
          )}

          {/* Default Content (Recent Searches & Popular Tags) */}
          {!searchQuery && (
            <div className="p-4 space-y-6">
              {/* Recent Searches */}
              {recentSearches.length > 0 && (
                <div>
                  <div className="flex items-center space-x-2 mb-3">
                    <Clock className="w-4 h-4 text-gray-500" />
                    <h3 className="font-medium text-gray-900">Recent Searches</h3>
                  </div>
                  <div className="space-y-1">
                    {recentSearches.slice(0, 5).map((search, index) => (
                      <button
                        key={index}
                        onClick={() => setSearchQuery(search)}
                        className="w-full flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors text-left"
                      >
                        <Search className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-700">{search}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Popular Tags */}
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <TrendingUp className="w-4 h-4 text-gray-500" />
                  <h3 className="font-medium text-gray-900">Popular Tags</h3>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {popularTags.slice(0, 8).map((tag, index) => (
                    <button
                      key={index}
                      onClick={() => onTagSelect?.(tag.tag, tag.category)}
                      className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left"
                    >
                      <Hash className="w-4 h-4 text-blue-600" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">#{tag.tag}</div>
                        <div className="text-xs text-gray-500">{tag.count} uses</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalSearch; 